/**
 * Centralized App Event Types & Labels
 *
 * This file contains the canonical mapping between raw snake_case event types
 * (as stored in the database) and their human-readable labels.
 *
 * Use these constants across:
 * - Customer timeline/event traces
 * - Session traces panel
 * - Step editor analytics
 * - Webhooks configuration
 * - Any other place that displays event types
 */

// ============================================================================
// Event Type Constants (as stored in database)
// ============================================================================

export const APP_EVENT_TYPES = {
  // Session Events
  CMS_SESSION_FIRST_INIT: 's_cms_session_first_init',
  CMS_SESSION_READY: 's_cms_session_ready',
  CMS_SESSION_LOADED: 's_cms_session_loaded',

  // Funnel/Step Events
  FUNNEL_SESSION_STARTED: 's_funnel_session_started',
  FUNNEL_SESSION_COMPLETED: 's_funnel_session_completed',
  FUNNEL_STARTED: 's_funnel_started',
  FUNNEL_STEP_VIEWED: 's_funnel_step_viewed',
  FUNNEL_STEP_ENTERED: 's_funnel_step_entered',
  FUNNEL_STEP_STARTED: 's_funnel_step_started',
  FUNNEL_STEP_COMPLETED: 's_funnel_step_completed',
  FUNNEL_STEP_CONVERTED: 's_funnel_step_converted',
  FUNNEL_COMPLETED: 's_funnel_completed',

  // Customer Events
  CUSTOMER_EMAIL_ENTERED: 's_customer_email_entered',
  CUSTOMER_IDENTIFIED: 's_customer_identified',

  // Checkout Events
  CHECKOUT_SESSION_CREATED: 's_checkout_session_created',
  CHECKOUT_STARTED: 's_checkout_started',
  CHECKOUT_CUSTOMER_EMAIL_VALID: 's_checkout_customer_email_valid',
  CHECKOUT_BILLING_ADDRESS_VALID: 's_checkout_billing_address_valid',
  CHECKOUT_SHIPPING_ADDRESS_VALID: 's_checkout_shipping_address_valid',
  CHECKOUT_SHIPPING_RATE_SELECTED: 's_checkout_shipping_rate_selected',

  // Order Events
  ORDER_CREATED: 's_order_created',
  ORDER_PAYMENT_INITIATED: 's_order_payment_initiated',
  ORDER_PAYMENT_FAILED: 's_order_payment_failed',
  ORDER_PAID: 's_order_paid',
  ORDER_FULFILLED: 's_order_fulfilled',
  ORDER_REFUNDED: 's_order_refunded',
  ORDER_CANCELED: 's_order_canceled',

  // Upsell Events
  UPSELL_VIEWED: 's_upsell_viewed',
  UPSELL_ACCEPTED: 's_upsell_accepted',
  UPSELL_DECLINED: 's_upsell_declined',

  // Button/Interaction Events
  BUTTON_CLICKED: 's_button_clicked',
  FORM_SUBMITTED: 's_form_submitted',

  // Payment Events
  PAYMENT_CREATED: 's_payment_created',
  PAYMENT_SUCCEEDED: 's_payment_succeeded',
  PAYMENT_FAILED: 's_payment_failed',
  PAYMENT_REFUNDED: 's_payment_refunded',
  PAYMENT_AUTHORIZED: 's_payment_authorized',
  PAYMENT_REJECTED: 's_payment_rejected',

  // Error Events
  PAYMENT_ERROR: 's_payment_error',
  CHECKOUT_ERROR: 's_checkout_error',
} as const;

export type AppEventType = (typeof APP_EVENT_TYPES)[keyof typeof APP_EVENT_TYPES];

// ============================================================================
// Event Labels (Human-readable)
// ============================================================================

export const APP_EVENT_LABELS: Record<string, string> = {
  // Session Events
  [APP_EVENT_TYPES.CMS_SESSION_FIRST_INIT]: 'Session Initialized',
  [APP_EVENT_TYPES.CMS_SESSION_READY]: 'Session Ready',
  [APP_EVENT_TYPES.CMS_SESSION_LOADED]: 'Session Loaded',

  // Funnel/Step Events
  [APP_EVENT_TYPES.FUNNEL_SESSION_STARTED]: 'Funnel Started',
  [APP_EVENT_TYPES.FUNNEL_SESSION_COMPLETED]: 'Funnel Completed',
  [APP_EVENT_TYPES.FUNNEL_STARTED]: 'Funnel Started',
  [APP_EVENT_TYPES.FUNNEL_STEP_VIEWED]: 'Step Viewed',
  [APP_EVENT_TYPES.FUNNEL_STEP_ENTERED]: 'Step Entered',
  [APP_EVENT_TYPES.FUNNEL_STEP_STARTED]: 'Step Started',
  [APP_EVENT_TYPES.FUNNEL_STEP_COMPLETED]: 'Step Completed',
  [APP_EVENT_TYPES.FUNNEL_STEP_CONVERTED]: 'Step Converted',
  [APP_EVENT_TYPES.FUNNEL_COMPLETED]: 'Funnel Completed',

  // Customer Events
  [APP_EVENT_TYPES.CUSTOMER_EMAIL_ENTERED]: 'Email Entered',
  [APP_EVENT_TYPES.CUSTOMER_IDENTIFIED]: 'Customer Identified',

  // Checkout Events
  [APP_EVENT_TYPES.CHECKOUT_SESSION_CREATED]: 'Checkout Started',
  [APP_EVENT_TYPES.CHECKOUT_STARTED]: 'Checkout Started',
  [APP_EVENT_TYPES.CHECKOUT_CUSTOMER_EMAIL_VALID]: 'Email Validated',
  [APP_EVENT_TYPES.CHECKOUT_BILLING_ADDRESS_VALID]: 'Billing Address Set',
  [APP_EVENT_TYPES.CHECKOUT_SHIPPING_ADDRESS_VALID]: 'Shipping Address Set',
  [APP_EVENT_TYPES.CHECKOUT_SHIPPING_RATE_SELECTED]: 'Shipping Selected',

  // Order Events
  [APP_EVENT_TYPES.ORDER_CREATED]: 'Order Created',
  [APP_EVENT_TYPES.ORDER_PAYMENT_INITIATED]: 'Order Payment Initiated',
  [APP_EVENT_TYPES.ORDER_PAYMENT_FAILED]: 'Order Payment Failed',
  [APP_EVENT_TYPES.ORDER_PAID]: 'Order Paid',
  [APP_EVENT_TYPES.ORDER_FULFILLED]: 'Order Fulfilled',
  [APP_EVENT_TYPES.ORDER_REFUNDED]: 'Order Refunded',
  [APP_EVENT_TYPES.ORDER_CANCELED]: 'Order Canceled',

  // Upsell Events
  [APP_EVENT_TYPES.UPSELL_VIEWED]: 'Upsell Viewed',
  [APP_EVENT_TYPES.UPSELL_ACCEPTED]: 'Upsell Accepted',
  [APP_EVENT_TYPES.UPSELL_DECLINED]: 'Upsell Declined',

  // Button/Interaction Events
  [APP_EVENT_TYPES.BUTTON_CLICKED]: 'Button Clicked',
  [APP_EVENT_TYPES.FORM_SUBMITTED]: 'Form Submitted',

  // Payment Events
  [APP_EVENT_TYPES.PAYMENT_CREATED]: 'Payment Created',
  [APP_EVENT_TYPES.PAYMENT_SUCCEEDED]: 'Payment Succeeded',
  [APP_EVENT_TYPES.PAYMENT_FAILED]: 'Payment Failed',
  [APP_EVENT_TYPES.PAYMENT_REFUNDED]: 'Payment Refunded',
  [APP_EVENT_TYPES.PAYMENT_AUTHORIZED]: 'Payment Authorized',
  [APP_EVENT_TYPES.PAYMENT_REJECTED]: 'Payment Rejected',

  // Error Events
  [APP_EVENT_TYPES.PAYMENT_ERROR]: 'Payment Error',
  [APP_EVENT_TYPES.CHECKOUT_ERROR]: 'Checkout Error',
};

// ============================================================================
// Event Categories
// ============================================================================

export type EventCategory = 'session' | 'funnel' | 'customer' | 'checkout' | 'order' | 'upsell' | 'interaction' | 'error' | 'unknown';

export interface EventCategoryConfig {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

export const EVENT_CATEGORIES: Record<EventCategory, EventCategoryConfig> = {
  session: {
    label: 'Session',
    color: 'text-sky-700 dark:text-sky-400',
    bgColor: 'bg-sky-100 dark:bg-sky-950',
    borderColor: 'border-sky-200 dark:border-sky-800',
  },
  funnel: {
    label: 'Funnel',
    color: 'text-purple-700 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-950',
    borderColor: 'border-purple-200 dark:border-purple-800',
  },
  customer: {
    label: 'Customer',
    color: 'text-amber-700 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-950',
    borderColor: 'border-amber-200 dark:border-amber-800',
  },
  checkout: {
    label: 'Checkout',
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-950',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  order: {
    label: 'Order',
    color: 'text-green-700 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-950',
    borderColor: 'border-green-200 dark:border-green-800',
  },
  upsell: {
    label: 'Upsell',
    color: 'text-pink-700 dark:text-pink-400',
    bgColor: 'bg-pink-100 dark:bg-pink-950',
    borderColor: 'border-pink-200 dark:border-pink-800',
  },
  interaction: {
    label: 'Interaction',
    color: 'text-orange-700 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-950',
    borderColor: 'border-orange-200 dark:border-orange-800',
  },
  error: {
    label: 'Error',
    color: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-950',
    borderColor: 'border-red-200 dark:border-red-800',
  },
  unknown: {
    label: 'Event',
    color: 'text-gray-700 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-900',
    borderColor: 'border-gray-200 dark:border-gray-700',
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the human-readable label for an event type
 * Falls back to formatting the raw event type if not found
 */
export function getEventLabel(eventType: string): string {
  // Check if we have a defined label
  if (APP_EVENT_LABELS[eventType]) {
    return APP_EVENT_LABELS[eventType];
  }

  // Fallback: format snake_case to Title Case
  return formatEventType(eventType);
}

/**
 * Format a raw event type to a readable string
 * Removes 's_' prefix and converts snake_case to Title Case
 */
export function formatEventType(eventType: string): string {
  return eventType
    .replace(/^s_/, '') // Remove s_ prefix
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
}

/**
 * Get the category for an event type
 */
export function getEventCategory(eventType: string): EventCategory {
  const type = eventType.toLowerCase();

  if (type.includes('cms_session') || type.includes('session_ready')) {
    return 'session';
  }
  if (type.includes('funnel') || type.includes('step')) {
    return 'funnel';
  }
  if (type.includes('customer') || type.includes('email_entered') || type.includes('identified')) {
    return 'customer';
  }
  if (type.includes('checkout') || type.includes('shipping') || type.includes('billing_address')) {
    return 'checkout';
  }
  if (type.includes('order') || type.includes('paid') || type.includes('payment')) {
    return 'order';
  }
  if (type.includes('upsell')) {
    return 'upsell';
  }
  if (type.includes('button') || type.includes('click') || type.includes('form')) {
    return 'interaction';
  }
  if (type.includes('error') || type.includes('fail')) {
    return 'error';
  }

  return 'unknown';
}

/**
 * Get the category config for an event type
 */
export function getEventCategoryConfig(eventType: string): EventCategoryConfig {
  const category = getEventCategory(eventType);
  return EVENT_CATEGORIES[category];
}

/**
 * Check if an event type indicates a conversion
 */
export function isConversionEvent(eventType: string): boolean {
  const type = eventType.toLowerCase();
  return type.includes('paid') || type.includes('order_paid') || type.includes('completed');
}

/**
 * Check if an event type indicates an error
 */
export function isErrorEvent(eventType: string): boolean {
  const type = eventType.toLowerCase();
  return type.includes('error') || type.includes('fail');
}

/**
 * Get icon name for an event type (for use with lucide-react)
 * Returns a string identifier that can be mapped to actual icons
 */
export type EventIconType =
  | 'credit-card'
  | 'shopping-cart'
  | 'mail'
  | 'mouse-pointer'
  | 'eye'
  | 'alert-circle'
  | 'check-circle'
  | 'user'
  | 'truck'
  | 'map-pin'
  | 'activity';

export function getEventIconType(eventType: string): EventIconType {
  const type = eventType.toLowerCase();

  if (type.includes('paid') || type.includes('payment')) {
    return 'credit-card';
  }
  if (type.includes('order') && !type.includes('payment')) {
    return 'shopping-cart';
  }
  if (type.includes('email')) {
    return 'mail';
  }
  if (type.includes('click') || type.includes('button')) {
    return 'mouse-pointer';
  }
  if (type.includes('view') || type.includes('init') || type.includes('start')) {
    return 'eye';
  }
  if (type.includes('error') || type.includes('fail')) {
    return 'alert-circle';
  }
  if (type.includes('success') || type.includes('complete') || type.includes('valid')) {
    return 'check-circle';
  }
  if (type.includes('customer') || type.includes('identified')) {
    return 'user';
  }
  if (type.includes('shipping')) {
    return 'truck';
  }
  if (type.includes('address') || type.includes('billing')) {
    return 'map-pin';
  }

  return 'activity';
}

